import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/* =========================================================================
   Centralized query + mutation hooks.
   Every data-fetching React component should use these — never call api.get
   directly from a page. That keeps caching + invalidation predictable.
   ========================================================================= */


/* ---------- Public menu ---------- */
export function useMenu() {
  return useQuery({
    queryKey: ["menu", "public"],
    queryFn: async () => (await api.get("/menu/public")).data,
    staleTime: 5 * 60_000, // menu rarely changes; keep fresh for 5 min
  });
}


/* ---------- Popular modifiers report ---------- */
export function usePopularModifiers({ days = 30, start = null, end = null } = {}) {
  const useRange = Boolean(start && end);
  return useQuery({
    queryKey: ["reports", "popular-modifiers", useRange ? `${start}~${end}` : `d${days}`],
    queryFn: async () => {
      const params = useRange ? { start, end } : { days };
      return (await api.get(`/reports/popular-modifiers`, { params })).data;
    },
    staleTime: 30_000,
  });
}


export function useTaxReport({ start, end, locationId = "all" }) {
  return useQuery({
    queryKey: ["reports", "tax", `${start}~${end}`, locationId],
    enabled: Boolean(start && end),
    queryFn: async () =>
      (await api.get(`/reports/tax`, { params: { start, end, location_id: locationId } })).data,
    staleTime: 30_000,
  });
}


/* ---------- Public settings (social handles, hours, locations…) ---------- */
export function usePublicSettings() {
  return useQuery({
    queryKey: ["settings", "public"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 5 * 60_000,
  });
}


/* ---------- Admin: payments config ---------- */
export function usePaymentsConfig() {
  return useQuery({
    queryKey: ["admin", "payments", "config"],
    queryFn: async () => (await api.get("/admin/payments/config")).data,
    staleTime: 30_000,
  });
}

export function useSavePaymentsConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await api.post("/admin/payments/config", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useTestStripeConnection() {
  return useMutation({
    mutationFn: async () => (await api.post("/admin/payments/test-connection")).data,
  });
}

export function useRegisterReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/payments/readers/register", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useUnregisterReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete("/admin/payments/readers")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useReaders() {
  return useQuery({
    queryKey: ["admin", "payments", "readers"],
    queryFn: async () => (await api.get("/admin/payments/readers")).data,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useActivateReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reader_id) =>
      (await api.post("/admin/payments/readers/activate", { reader_id })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function usePrinterConfig() {
  return useQuery({
    queryKey: ["admin", "payments", "printer"],
    queryFn: async () => (await api.get("/admin/payments/printer")).data,
    staleTime: 60_000,
  });
}

export function useSavePrinterConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/payments/printer", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useMenuBoardLayout() {
  return useQuery({
    queryKey: ["admin", "menu-board", "layout"],
    queryFn: async () => (await api.get("/admin/menu-board/layout")).data,
  });
}

export function useSaveMenuBoardLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (layout) => (await api.put("/admin/menu-board/layout", layout)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "menu-board", "layout"] }),
  });
}

export function useTestDrawerKick() {
  return useMutation({
    mutationFn: async () => (await api.post("/admin/payments/printer/test-kick")).data,
  });
}

export function useBridgeStatus() {
  return useQuery({
    queryKey: ["admin", "payments", "bridge"],
    queryFn: async () => (await api.get("/admin/payments/bridge/status")).data,
    refetchInterval: 5_000,
  });
}

export function useToggleBridge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled) => (await api.post("/admin/payments/bridge/toggle", { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments", "bridge"] }),
  });
}

export function useRotateBridgeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/admin/payments/bridge/token")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments", "bridge"] }),
  });
}

export function useToggleOnReaderLoyalty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled) =>
      (await api.post("/admin/payments/loyalty-toggle", { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}


/* ---------- Admin: Loyalty (config / rewards / adjust / stats) ---------- */
export function useLoyaltyConfig() {
  return useQuery({
    queryKey: ["admin", "loyalty", "config"],
    queryFn: async () => (await api.get("/admin/loyalty/config")).data,
    staleTime: 30_000,
  });
}

export function useSaveLoyaltyConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/loyalty/config", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "loyalty"] });
      // payments page also reads on_reader_loyalty_enabled via /payments/config
      qc.invalidateQueries({ queryKey: ["admin", "payments", "config"] });
    },
  });
}

export function useLoyaltyRewards({ includeInactive = true } = {}) {
  return useQuery({
    queryKey: ["admin", "loyalty", "rewards", { includeInactive }],
    queryFn: async () =>
      (await api.get(`/admin/loyalty/rewards?include_inactive=${includeInactive}`)).data,
    staleTime: 30_000,
  });
}

export function useSaveLoyaltyReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? (await api.patch(`/admin/loyalty/rewards/${id}`, body)).data
        : (await api.post("/admin/loyalty/rewards", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "loyalty", "rewards"] }),
  });
}

export function useDeleteLoyaltyReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/loyalty/rewards/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "loyalty", "rewards"] }),
  });
}

export function usePushRewardToSplash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rewardId) =>
      (await api.post(`/admin/loyalty/rewards/${rewardId}/push-splash`)).data,
    onSuccess: () => {
      // Splash card on /admin/payments mirrors the same data
      qc.invalidateQueries({ queryKey: ["admin", "payments", "splash"] });
    },
  });
}

export function useEmailRewardPromo() {
  return useMutation({
    mutationFn: async ({ rewardId, body }) =>
      (await api.post(`/admin/loyalty/rewards/${rewardId}/email-promo`, body || {})).data,
  });
}

export function useRewardEmailRecipients(rewardId, enabled = true) {
  return useQuery({
    queryKey: ["admin", "loyalty", "rewards", rewardId, "recipients"],
    queryFn: async () =>
      (await api.get(`/admin/loyalty/rewards/${rewardId}/email-recipients`)).data,
    enabled: !!rewardId && enabled,
    staleTime: 30_000,
  });
}


/* ---------- Customer-facing menu board ---------- */
export function useMenuBoardState({ refetchInterval = 15_000 } = {}) {
  // Public endpoint — use the existing axios instance with credentials
  // disabled for this one request, so we match the auth/header behaviour
  // of every other hook (interceptors, base URL handling, etc).
  return useQuery({
    queryKey: ["menu-board", "state"],
    queryFn: async () => {
      const res = await api.get("/menu-board/state", { withCredentials: false });
      return res.data;
    },
    retry: 1,
    refetchInterval,
    staleTime: refetchInterval / 2,
  });
}

export function useSetItemAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, available }) =>
      (await api.patch(`/menu/items/${itemId}/availability`, { available })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useSetOptionAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, optionId, available }) =>
      (await api.patch(
        `/menu/modifier-groups/${groupId}/options/${optionId}/availability`,
        { available },
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function usePearlTimer({ refetchInterval = 5_000 } = {}) {
  return useQuery({
    queryKey: ["admin", "pearl-timer"],
    queryFn: async () => (await api.get("/admin/menu-board/pearl-timer")).data,
    refetchInterval,
  });
}

export function useStartPearlTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      (await api.post("/admin/menu-board/pearl-timer", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pearl-timer"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useExtendPearlTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (minutes) =>
      (await api.patch("/admin/menu-board/pearl-timer/extend", { minutes })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pearl-timer"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useClearPearlTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.delete("/admin/menu-board/pearl-timer")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pearl-timer"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}


/* ---------- Drink-of-the-day spotlight ---------- */
export function useFeaturedDrink() {
  return useQuery({
    queryKey: ["admin", "featured-drink"],
    queryFn: async () => (await api.get("/admin/menu-board/featured")).data,
    staleTime: 30_000,
  });
}

export function useSaveFeaturedDrink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      (await api.post("/admin/menu-board/featured", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "featured-drink"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useToggleFeaturedDrink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled) =>
      (await api.patch("/admin/menu-board/featured/toggle", { enabled })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "featured-drink"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useClearFeaturedDrink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.delete("/admin/menu-board/featured")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "featured-drink"] });
      qc.invalidateQueries({ queryKey: ["menu-board"] });
    },
  });
}

export function useAdjustLoyaltyPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/loyalty/adjust", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "loyalty", "stats"] });
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    },
  });
}

export function useLoyaltyStats() {
  return useQuery({
    queryKey: ["admin", "loyalty", "stats"],
    queryFn: async () => (await api.get("/admin/loyalty/stats")).data,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useTippingConfig() {
  return useQuery({
    queryKey: ["admin", "payments", "tipping"],
    queryFn: async () => (await api.get("/admin/payments/tipping")).data,
    staleTime: 60_000,
  });
}

export function useSaveTippingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/payments/tipping", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useSplashConfig() {
  return useQuery({
    queryKey: ["admin", "payments", "splash"],
    queryFn: async () => (await api.get("/admin/payments/splash")).data,
    staleTime: 60_000,
  });
}

export function useUploadSplash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/payments/splash", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

export function useRemoveSplash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete("/admin/payments/splash")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}

/* -------- Uploads (object storage) -------- */

/**
 * Upload a single image file and return the public URL. Uses a progress
 * hook if the caller wants to render a bar.
 */
export async function uploadImage(file, { kind = "appearances", onProgress } = {}) {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post(`/admin/uploads/image?kind=${encodeURIComponent(kind)}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
}

/* -------- Appearances (public calendar of pop-ups) -------- */

export function useUpcomingAppearances(limit = 20) {
  return useQuery({
    queryKey: ["appearances", "upcoming", limit],
    queryFn: async () =>
      (await api.get(`/appearances/upcoming?limit=${limit}`)).data,
    staleTime: 60_000,
  });
}

export function useAllAppearances({ includePast = true } = {}) {
  return useQuery({
    queryKey: ["admin", "appearances", { includePast }],
    queryFn: async () =>
      (await api.get(`/admin/appearances?include_past=${includePast}`)).data,
    staleTime: 30_000,
  });
}

export function useAppearanceFollowUps() {
  return useQuery({
    queryKey: ["admin", "appearances", "follow-ups"],
    queryFn: async () =>
      (await api.get("/admin/appearances/follow-ups")).data,
    staleTime: 30_000,
  });
}

export function useCreateAppearance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/appearances", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "appearances"] });
      qc.invalidateQueries({ queryKey: ["appearances", "upcoming"] });
    },
  });
}

export function useUpdateAppearance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      (await api.patch(`/admin/appearances/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "appearances"] });
      qc.invalidateQueries({ queryKey: ["appearances", "upcoming"] });
    },
  });
}

export function useDeleteAppearance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/appearances/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "appearances"] });
      qc.invalidateQueries({ queryKey: ["appearances", "upcoming"] });
    },
  });
}

export function useToggleOffline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled) => (await api.post("/admin/payments/offline-mode", { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments"] }),
  });
}


/* ---------- Admin: webhook activity ---------- */
export function useWebhookActivity(limit = 50) {
  return useQuery({
    queryKey: ["admin", "payments", "webhooks", limit],
    queryFn: async () =>
      (await api.get(`/admin/payments/webhooks/recent?limit=${limit}`)).data,
    staleTime: 15_000,
    refetchInterval: 30_000, // gentle polling so operators see new events live
  });
}

export function useRetryWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventId) =>
      (await api.post(`/admin/payments/webhooks/${eventId}/retry`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "payments", "webhooks"] }),
  });
}


/* ---------- Rewards (customer) ---------- */
export function useMyTier(enabled = true) {
  return useQuery({
    queryKey: ["loyalty", "tier", "me"],
    queryFn: async () => (await api.get("/loyalty/tier")).data,
    enabled,
    staleTime: 30_000,
  });
}


/* ---------- Settings ---------- */
export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
}


/* ---------- Admin: Staff users ---------- */
export function useStaffUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => (await api.get("/admin/users")).data,
    staleTime: 30_000,
  });
}

export function useCreateStaffUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/admin/users", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useUpdateStaffUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/admin/users/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useResetStaffPassword() {
  return useMutation({
    mutationFn: async ({ id, password }) =>
      (await api.post(`/admin/users/${id}/password`, { password })).data,
  });
}

export function useDeleteStaffUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useResendStaffInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/admin/users/${id}/resend-invite`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function usePermissionCatalog() {
  return useQuery({
    queryKey: ["admin", "permissions", "catalog"],
    queryFn: async () => (await api.get("/admin/users/permissions/catalog")).data,
    staleTime: 5 * 60_000,
  });
}


/* ---------- Admin: Inventory ---------- */
const inventoryKeys = {
  ingredients: ["admin", "inventory", "ingredients"],
  vendors:     ["admin", "inventory", "vendors"],
  adjustments: ["admin", "inventory", "adjustments"],
};

export function useIngredients(locationId = "all") {
  return useQuery({
    queryKey: [...inventoryKeys.ingredients, locationId],
    queryFn: async () =>
      (await api.get("/inventory/ingredients", { params: { location_id: locationId } })).data,
    staleTime: 30_000,
  });
}

export function useVendors() {
  return useQuery({
    queryKey: inventoryKeys.vendors,
    queryFn: async () => (await api.get("/inventory/vendors")).data,
    staleTime: 60_000,
  });
}

export function useAdjustments(locationId = "all") {
  return useQuery({
    queryKey: [...inventoryKeys.adjustments, locationId],
    queryFn: async () =>
      (await api.get("/inventory/adjustments", { params: { location_id: locationId } })).data,
    staleTime: 30_000,
  });
}

function invalidateInventory(qc) {
  qc.invalidateQueries({ queryKey: ["admin", "inventory"] });
  qc.invalidateQueries({ queryKey: ["admin", "reports"] });
}

export function useSaveIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body, locationId }) => {
      const params = locationId && locationId !== "all" ? { location_id: locationId } : {};
      return id
        ? (await api.patch(`/inventory/ingredients/${id}`, body, { params })).data
        : (await api.post("/inventory/ingredients", body, { params })).data;
    },
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useTransferStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/inventory/transfer", body)).data,
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useDeleteIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/inventory/ingredients/${id}`)).data,
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useSaveVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? (await api.patch(`/inventory/vendors/${id}`, body)).data
        : (await api.post("/inventory/vendors", body)).data,
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/inventory/vendors/${id}`)).data,
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useCreateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/inventory/adjustments", body)).data,
    onSuccess: () => invalidateInventory(qc),
  });
}


/* ---------- Admin: Reports ---------- */
export function useReportsSummary(locationId = "all") {
  return useQuery({
    queryKey: ["admin", "reports", "summary", locationId],
    queryFn: async () =>
      (await api.get("/reports/summary", { params: { location_id: locationId } })).data,
    staleTime: 60_000,        // dashboard freshness
    refetchInterval: 5 * 60_000, // background refresh every 5 min so totals stay current
  });
}

export function useLocationReports() {
  return useQuery({
    queryKey: ["admin", "reports", "by-location"],
    queryFn: async () => (await api.get("/reports/by-location")).data,
    staleTime: 60_000,
  });
}

export function usePopupReports() {
  return useQuery({
    queryKey: ["admin", "reports", "popups"],
    queryFn: async () => (await api.get("/reports/popups")).data,
    staleTime: 60_000,
  });
}


/* ---------- Admin: Orders ---------- */
export function useAdminAppearances(enabled = true) {
  return useQuery({
    queryKey: ["admin", "appearances", "all"],
    queryFn: async () =>
      (await api.get("/admin/appearances", { params: { include_past: true, limit: 500 } })).data,
    enabled,
    staleTime: 60_000,
  });
}

export function useOrders(status = null, limit = 100) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (limit) qs.set("limit", String(limit));
  return useQuery({
    queryKey: ["admin", "orders", status || "all", limit],
    queryFn: async () => (await api.get(`/orders?${qs.toString()}`)).data,
    staleTime: 10_000,
    refetchInterval: 30_000, // gently keep the list fresh
  });
}

export function useOrder(orderId, enabled = true) {
  return useQuery({
    queryKey: ["admin", "order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
    enabled: !!orderId && enabled,
    staleTime: 5_000,
  });
}


/* ---------- Admin: Customers ---------- */
export function useCustomers(query = "") {
  const qs = query ? `?q=${encodeURIComponent(query)}` : "";
  return useQuery({
    queryKey: ["admin", "customers", query || "all"],
    queryFn: async () => (await api.get(`/customers${qs}`)).data,
    staleTime: 30_000,
    // De-dup repeated typing via React Query's natural keying.
  });
}


/* ---------- Admin: Event inquiries ---------- */
export function useEventInquiries(status = "") {
  const qs = status ? `?status=${status}` : "";
  return useQuery({
    queryKey: ["admin", "events", status || "all"],
    queryFn: async () => (await api.get(`/admin/events${qs}`)).data,
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

export function useUpdateEventInquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      (await api.patch(`/admin/events/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "events"] }),
  });
}

export function useDeleteEventInquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/events/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "events"] }),
  });
}

/* ---------- Gift cards (stored value) ---------- */
export function useGiftCards(q = "") {
  return useQuery({
    queryKey: ["admin", "gift-cards", q],
    queryFn: async () => (await api.get("/gift-cards", { params: q ? { q } : {} })).data,
    staleTime: 30_000,
  });
}

export function useIssueGiftCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/gift-cards/issue", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "gift-cards"] }),
  });
}

export function useReloadGiftCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount_cents, note }) =>
      (await api.post(`/gift-cards/${id}/reload`, { amount_cents, note })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "gift-cards"] }),
  });
}

export function useAdjustGiftCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, delta_cents, reason }) =>
      (await api.post(`/gift-cards/${id}/adjust`, { delta_cents, reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "gift-cards"] }),
  });
}

export function useSetGiftCardStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }) =>
      (await api.patch(`/gift-cards/${id}/status`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "gift-cards"] }),
  });
}
